<script>
import { useAlert } from 'dashboard/composables';
import { getCentralHeaders } from 'dashboard/helpers/centralApiHeaders';

export default {
  name: 'SelectContact',
  props: {
    value: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      contactQuery: '',
      chatList: [],
      isDropdownOpen: false,
      contactCurrentPage: 1,
      contactCount: 0,
      contactPerPage: 0,
      contactTotalPages: 0,
      contactNextPage: false,
      skipNextQueryWatch: false,
    };
  },
  computed: {
    getCurrentAccountId() {
      return this.$store.getters['getCurrentAccountId'];
    },
    selectedContact: {
      get() {
        return this.value || null;
      },
      set(val) {
        this.$emit('input', val);
      }
    },
  },
  watch: {
    async contactCurrentPage() {
      await this.fetchChatLists();
    },
    async contactQuery() {
      if (this.skipNextQueryWatch) {
        this.skipNextQueryWatch = false;
        return;
      }
      this.contactCurrentPage = 1;
      await this.fetchChatLists();
    },
    value(newVal) {
      this.contactQuery = newVal?.name || '';
    }
  },
  methods: {
    async fetchChatLists() {
      try {
        const url = new URL(`${window.location.origin}/api/v1/accounts/${this.getCurrentAccountId}/appointments/contacts`);
        url.searchParams.append('page', this.contactCurrentPage);
        url.searchParams.append('q', this.contactQuery);
        const rawResponse = await fetch(url.toString(), {
          headers: {
            ...getCentralHeaders(),
          }
        });
        if (!rawResponse.ok) {
          const errorData = await rawResponse.json();
          throw new Error(errorData.detail);
        }
        const response = await rawResponse.json();
        this.chatList = response.contacts;
        this.contactCount = response.meta.count;
        this.contactPerPage = response.meta.per_page;
        this.contactTotalPages = response.meta.total_pages;
        this.contactNextPage = response.meta.next;
      } catch (err) {
        console.error('Error fetching contacts:', err);
        useAlert('Failed to load contacts');
        this.chatList = [];
      }
    },
    showInboxLogo(inbox) {
      switch(inbox) {
        case 'website':
          return 'globe';
        case 'facebook':
          return 'brand-facebook'; 
        case 'twitter':
          return 'brand-twitter';
        case 'whatsapp':
          return 'brand-whatsapp';
        case 'api':
          return 'code';
        case 'email':
          return 'mail';
        case 'telegram':
          return 'brand-telegram';
        case 'line':
          return 'brand-line';
        case 'sms':
          return 'brand-sms';
        case 'phone':
          return 'call';
        case 'instagram':
          return 'brand-instagram';
        default:
          return 'globe';
      }
    },
    selectContact(contact) {
      this.selectedContact = contact;
      this.skipNextQueryWatch = true;
      this.contactQuery = contact.name;
      this.$emit('selected', contact);
      this.isDropdownOpen = false;
    },
    closeDropdownWithDelay() {
      setTimeout(() => {
        this.isDropdownOpen = false;
      }, 200);
    },
    nextPage() {
      if (this.contactNextPage) {
        this.contactCurrentPage++;
      }
    },
    prevPage() {
      if (this.contactCurrentPage > 1) {
        this.contactCurrentPage--;
      }
    }
  },
  async mounted() {
    await this.fetchChatLists();
  }
};
</script>

<template>
  <label class="block relative mb-4">
    <span class="text-sm font-medium text-slate-700 dark:text-slate-300">
      Select Customer
    </span>
    <div
      class="overflow-hidden border-slate-200 mt-1 w-full border rounded-md text-slate-900 dark:text-white dark:bg-slate-700 dark:border-slate-600 focus-within:ring-2 focus-within:ring-woot-500"
    >
      <input
        type="text"
        v-model="contactQuery"
        class="reset-base w-full p-2 bg-white dark:bg-transparent border-none focus:outline-none"
        placeholder="Search customer..."
        @focus="isDropdownOpen = true"
        @blur="closeDropdownWithDelay"
        @click="isDropdownOpen = true"
      />
      <div
        v-if="isDropdownOpen"
        class="fixed inset-0 z-40"
        @click="isDropdownOpen = false"
      ></div>
      <div
        v-if="isDropdownOpen"
        class="absolute z-50 left-0 right-0 mt-1 bg-white dark:bg-slate-700 border rounded-md shadow-lg max-h-60 overflow-y-auto"
      >
        <div v-if="chatList.length === 0" class="p-2 text-slate-500">
          No customers found
        </div>
        <div v-else>
          <div
            v-for="customer in chatList"
            :key="customer.id"
            class="p-3 hover:bg-slate-100 dark:hover:bg-slate-600 cursor-pointer customer-option border-b border-slate-200 dark:border-slate-700"
            @click.prevent="selectContact(customer)"
          >
            <!-- Name row with optional inbox-icon + phone -->
            <div class="text-sm font-medium text-slate-900 dark:text-white flex gap-1 items-center">
              <fluent-icon
                v-if="customer.conversations && customer.conversations.length"
                size="14"
                :icon="showInboxLogo(customer.conversations[0].inbox_channel)"
              />
              {{ customer.name }}
              <span v-if="customer.phone_number" class="text-xs text-slate-500 dark:text-slate-400">
                ({{ customer.phone_number }})
              </span>
            </div>

            <!-- Inbox name under the name -->
            <div
              v-if="customer.conversations && customer.conversations.length"
              class="text-xs text-slate-500 dark:text-slate-400"
            >
              {{ customer.conversations[0].inbox_name || '—' }}
            </div>
          </div>

          <!-- Pagination footer -->
          <div class="flex items-center justify-between p-2 border-t">
            <button
              type="button"
              class="px-2 py-1 text-sm bg-slate-200 dark:bg-slate-600 rounded disabled:opacity-50"
              @mousedown.prevent="prevPage"
              :disabled="contactCurrentPage === 1"
            >
              Previous
            </button>

            <span class="text-sm text-slate-600 dark:text-slate-400">
              Page {{ contactCurrentPage }} / {{ contactTotalPages }}
            </span>

            <button
              type="button"
              class="px-2 py-1 text-sm bg-slate-200 dark:bg-slate-600 rounded disabled:opacity-50"
              @mousedown.prevent="nextPage"
              :disabled="!contactNextPage"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  </label>
</template>
